import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import {
  signInWithRedirect,
  signInWithPopup,
  getRedirectResult,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  sendPasswordResetEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  sendEmailVerification,
  type User as FirebaseUser,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, googleProvider, db } from "@/lib/firebase";
import { UserRole, type User } from "@shared/schema";
import { requestFCMToken, listenForMessages } from "@/lib/notifications";

interface AuthContextType {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  signIn: (rememberMe?: boolean) => Promise<User | null>;
  signInWithEmail: (email: string, password: string, rememberMe?: boolean) => Promise<User | null>;
  signUpWithEmail: (email: string, password: string, displayName: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  isCustomer: boolean;
  isEmployee: boolean;
  isCashier: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirectProcessed, setRedirectProcessed] = useState(!localStorage.getItem('google_redirect_pending'));

  // Fetch user data from Firestore
  const fetchUserData = async (fbUser: FirebaseUser): Promise<User | null> => {
    try {
      
      const userRef = doc(db, "users", fbUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data() as User;
        
        // Update displayName and photoURL from Google if they exist and different
        const needsUpdate = 
          (fbUser.displayName && fbUser.displayName !== userData.displayName) ||
          (fbUser.photoURL && fbUser.photoURL !== userData.photoURL);
        
        if (needsUpdate) {
          const updates: Partial<User> = {};
          if (fbUser.displayName && fbUser.displayName !== userData.displayName) {
            updates.displayName = fbUser.displayName;
          }
          if (fbUser.photoURL && fbUser.photoURL !== userData.photoURL) {
            updates.photoURL = fbUser.photoURL;
          }
          
          await setDoc(userRef, updates, { merge: true });
          
          return { ...userData, id: fbUser.uid, ...updates };
        }
        
        return { ...userData, id: fbUser.uid };
      } else {
        
        // Create new user with default role: customer
        const newUser: User = {
          id: fbUser.uid,
          email: fbUser.email || "",
          displayName: fbUser.displayName || "Kullanıcı",
          photoURL: fbUser.photoURL,
          role: UserRole.CUSTOMER,
          createdAt: Date.now(),
        };

        // Save to Firestore
        await setDoc(userRef, newUser);
        
        return newUser;
      }
    } catch (error: any) {
      throw error;
    }
  };

  // Handle redirect result on page load (for Google OAuth redirect flow)
  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        console.log('[Auth] Checking redirect result...');
        const result = await getRedirectResult(auth);
        
        if (result) {
          console.log('[Auth] Redirect result found, processing user...');
          // User successfully signed in via redirect
          const userData = await fetchUserData(result.user);
          setUser(userData);
          setFirebaseUser(result.user);
          console.log('[Auth] User set from redirect result:', userData?.email);
        } else {
          console.log('[Auth] No redirect result (user may already be signed in via onAuthStateChanged)');
        }
      } catch (error: any) {
        console.error("[Auth] Redirect result error:", error);
        localStorage.setItem('login_error', JSON.stringify({
          code: error.code,
          message: error.message,
          timestamp: Date.now()
        }));
      } finally {
        // Always clear pending state and mark redirect as processed
        localStorage.removeItem('google_redirect_pending');
        setRedirectProcessed(true);
        console.log('[Auth] Redirect processing complete');
      }
    };
    
    // Only check redirect result if we have a pending redirect
    if (localStorage.getItem('google_redirect_pending')) {
      handleRedirectResult();
    }
  }, []);

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      console.log('[Auth] onAuthStateChanged triggered, user:', fbUser?.email || 'null');
      
      try {
        if (fbUser) {
          const userData = await fetchUserData(fbUser);
          setUser(userData);
          setFirebaseUser(fbUser);
          console.log('[Auth] User authenticated:', userData?.email);
          
          // Clear pending redirect state on successful auth
          localStorage.removeItem('google_redirect_pending');
          setRedirectProcessed(true);
          
          // Request FCM token for notifications (non-blocking)
          try {
            await requestFCMToken(fbUser.uid);
            
            // Listen for foreground messages
            listenForMessages((payload) => {
            });
          } catch (fcmError) {
            // FCM is optional - don't block authentication
          }
        } else {
          // No authenticated user - check for guest user
          const guestId = localStorage.getItem("guestId");
          
          if (guestId && guestId.startsWith("guest_")) {
            try {
              // Fetch guest user data from Firestore
              const guestUserRef = doc(db, "users", guestId);
              const guestUserSnap = await getDoc(guestUserRef);
              
              if (guestUserSnap.exists()) {
                const guestUserData = guestUserSnap.data() as User;
                setUser({ ...guestUserData, id: guestId });
              } else {
                setUser(null);
              }
            } catch (error) {
              setUser(null);
            }
          } else {
            setUser(null);
          }
          
          setFirebaseUser(null);
        }
      } catch (error: any) {
        // Sign out on persistent errors to prevent stuck state
        await firebaseSignOut(auth);
        setUser(null);
        setFirebaseUser(null);
        
        // Store error for display
        localStorage.setItem('auth_error', JSON.stringify({
          code: error.code || 'unknown',
          message: error.message || 'Authentication failed',
          timestamp: Date.now()
        }));
      } finally {
        // Only set loading to false if redirect has been processed
        // This prevents premature loading=false before redirect result is checked
        if (redirectProcessed) {
          setLoading(false);
        }
      }
    });

    return unsubscribe;
  }, [redirectProcessed]);

  const signIn = async (rememberMe: boolean = true): Promise<User | null> => {
    try {
      // Set persistence based on rememberMe
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
      
      // Try popup first - it's more reliable when it works
      try {
        console.log('[Auth] Attempting popup sign-in...');
        const result = await signInWithPopup(auth, googleProvider);
        console.log('[Auth] Popup sign-in successful');
        
        const userData = await fetchUserData(result.user);
        setUser(userData);
        setFirebaseUser(result.user);
        
        return userData;
      } catch (popupError: any) {
        console.log('[Auth] Popup failed:', popupError.code);
        
        // If popup was blocked or closed, fall back to redirect
        if (popupError.code === 'auth/popup-blocked' || 
            popupError.code === 'auth/popup-closed-by-user' ||
            popupError.code === 'auth/cancelled-popup-request') {
          console.log('[Auth] Falling back to redirect...');
          
          // Mark that we're starting a redirect flow
          localStorage.setItem('google_redirect_pending', 'true');
          
          await signInWithRedirect(auth, googleProvider);
          return null;
        }
        
        // Re-throw other errors
        throw popupError;
      }
    } catch (error: any) {
      console.error('[Auth] Sign-in error:', error);
      
      // Clear pending state on error
      localStorage.removeItem('google_redirect_pending');
      
      // Store error for display
      localStorage.setItem('login_error', JSON.stringify({
        code: error.code,
        message: error.message,
        timestamp: Date.now()
      }));
      
      throw error;
    }
  };

  const signInWithEmail = async (email: string, password: string, rememberMe: boolean = true): Promise<User | null> => {
    try {
      // Set persistence based on rememberMe
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
      
      const result = await signInWithEmailAndPassword(auth, email, password);
      const userData = await fetchUserData(result.user);
      setUser(userData);
      setFirebaseUser(result.user);
      
      // Return user data for immediate use
      return userData;
    } catch (error) {
      throw error;
    }
  };

  const signUpWithEmail = async (email: string, password: string, displayName: string) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Create user document with display name
      const newUser: User = {
        id: result.user.uid,
        email: result.user.email || "",
        displayName: displayName,
        photoURL: null,
        role: UserRole.CUSTOMER,
        createdAt: Date.now(),
      };

      // Save to Firestore
      const userRef = doc(db, "users", result.user.uid);
      await setDoc(userRef, newUser);
      
      setUser(newUser);
      setFirebaseUser(result.user);
    } catch (error: any) {
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setFirebaseUser(null);
    } catch (error) {
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw error;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      if (!firebaseUser || !firebaseUser.email) {
        throw new Error("No user logged in");
      }

      // Re-authenticate user first (Firebase requirement for sensitive operations)
      const credential = EmailAuthProvider.credential(
        firebaseUser.email,
        currentPassword
      );
      await reauthenticateWithCredential(firebaseUser, credential);

      // Update password
      await updatePassword(firebaseUser, newPassword);
    } catch (error) {
      throw error;
    }
  };

  const sendVerificationEmail = async () => {
    try {
      if (!firebaseUser) {
        throw new Error("No user logged in");
      }
      await sendEmailVerification(firebaseUser);
    } catch (error) {
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    firebaseUser,
    loading,
    signIn,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    resetPassword,
    changePassword,
    sendVerificationEmail,
    isCustomer: user?.role === UserRole.CUSTOMER,
    isEmployee: user?.role === UserRole.EMPLOYEE,
    isCashier: user?.role === UserRole.CASHIER,
    isAdmin: user?.role === UserRole.ADMIN,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
